import type { UIMessageChunk } from 'ai'

/** Pause between released words, roughly the AI SDK's `smoothStream()` default. */
export const WORD_DELAY_MS = 20

/** A word and the whitespace after it; a trailing partial word waits for more text or a flush. */
const WORD = /\S+\s+/

type DeltaChunk = Extract<UIMessageChunk, { type: 'text-delta' | 'reasoning-delta' }>

function isDelta(chunk: UIMessageChunk): chunk is DeltaChunk {
  return chunk.type === 'text-delta' || chunk.type === 'reasoning-delta'
}

/**
 * Re-times text and reasoning deltas so a reply appears word by word instead of in bursts, the client-side
 * counterpart of the AI SDK's server-side `smoothStream()`, which pydantic-ai does not offer. Deltas are
 * buffered per part and released one word every `delayMs`. Any other chunk, a delta for a different part, and
 * the end of the stream first release whatever is buffered, so nothing is held back or reordered.
 */
export function createPacingTransform(
  delayMs: number = WORD_DELAY_MS,
  wait: (ms: number) => Promise<void> = sleep
): TransformStream<UIMessageChunk, UIMessageChunk> {
  let pending: DeltaChunk | undefined

  const flush = (controller: TransformStreamDefaultController<UIMessageChunk>) => {
    if (pending?.delta) controller.enqueue(pending)
    pending = undefined
  }

  return new TransformStream<UIMessageChunk, UIMessageChunk>({
    async transform(chunk, controller) {
      if (!isDelta(chunk)) {
        flush(controller)
        controller.enqueue(chunk)
        return
      }
      if (pending && (pending.type !== chunk.type || pending.id !== chunk.id)) flush(controller)
      pending = { ...chunk, delta: (pending?.delta ?? '') + chunk.delta }

      let match = WORD.exec(pending.delta)
      while (match) {
        const word = pending.delta.slice(0, match.index + match[0].length)
        controller.enqueue({ ...pending, delta: word })
        pending = { ...pending, delta: pending.delta.slice(word.length) }
        await wait(delayMs)
        match = WORD.exec(pending.delta)
      }
    },
    flush
  })
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}
